import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  PlayCircleFilled,
  Close,
  GetApp,
  MoreVert,
  MusicNote,
  ChevronLeft,
  ChevronRight,
  PictureAsPdf,
} from '@material-ui/icons';
import { MessageAttachment } from '~/interfaces/conversations';
import { electron, log } from '~/helpers';
import AudioPlayer from '../AudioPlayer';
import {
  Grid,
  GridItem,
  GridThumb,
  GridVideoThumb,
  // GridVideoPlaceholder,
  GridVideoPlayOverlay,
  GridAudioThumb,
  GridPdfThumb,
  GridThumbLabel,
  OverflowBadge,
  LightboxBackdrop,
  LightboxImage,
  LightboxVideo,
  LightboxAudio,
  LightboxPdfFrame,
  LightboxNavButton,
  LightboxCounter,
  LightboxActions,
  LightboxIconButton,
  LightboxMenu,
  LightboxMenuItem,
} from './styles';

const MAX_VISIBLE = 4;

const isImage = (attachment: MessageAttachment): boolean =>
  attachment.mediaType === 'image';

const isPdf = (attachment: MessageAttachment): boolean =>
  attachment.mimeType === 'application/pdf' ||
  (attachment.filename?.toLowerCase().endsWith('.pdf') ?? false);

const isAudio = (attachment: MessageAttachment): boolean =>
  attachment.mediaType === 'audio';

const isVideo = (attachment: MessageAttachment): boolean =>
  attachment.mediaType === 'video';

interface MediaGroupProps {
  attachments: MessageAttachment[];
}

type GridLayout = 'single' | 'double' | 'triple' | 'quad';

function getLayout(count: number): GridLayout {
  if (count === 1) return 'single';
  if (count === 2) return 'double';
  if (count === 3) return 'triple';
  return 'quad';
}

const MediaGroup: React.FC<MediaGroupProps> = ({ attachments }) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const count = attachments.length;
  const visible = attachments.slice(0, MAX_VISIBLE);
  const overflowCount = count - MAX_VISIBLE;
  const layout = getLayout(Math.min(count, MAX_VISIBLE));

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setIsMenuOpen(false);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
    setIsMenuOpen(false);
  };

  const goTo = useCallback(
    (index: number) => {
      setLightboxIndex(((index % count) + count) % count);
      setIsMenuOpen(false);
    },
    [count]
  );

  React.useEffect(() => {
    if (lightboxIndex === null) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goTo(lightboxIndex + 1);
      if (e.key === 'ArrowLeft') goTo(lightboxIndex - 1);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [lightboxIndex, goTo]);

  const handleDownload = async (e: React.MouseEvent) => {
    const attachment =
      lightboxIndex !== null ? attachments[lightboxIndex] : null;
    if (!attachment) return;
    e.stopPropagation();
    setIsMenuOpen(false);

    if (window.isElectron) {
      electron.downloadFile({
        url: attachment.contentUrl,
        filename: attachment.filename,
      });
      return;
    }

    const triggerLink = (href: string, openInNewTab: boolean) => {
      const link = document.createElement('a');
      link.href = href;
      link.download = attachment.filename || 'download';
      if (openInNewTab) {
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
      }
      document.body.appendChild(link);
      link.click();
      link.remove();
    };

    try {
      const response = await fetch(attachment.contentUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      triggerLink(blobUrl, false);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      log.warn(
        'Media blob fetch blocked (likely CORS). Falling back to direct link.',
        err
      );
      triggerLink(attachment.contentUrl, true);
    }
  };

  const renderThumb = (attachment: MessageAttachment, index: number) => {
    const isOverflowSlot = index === MAX_VISIBLE - 1 && overflowCount > 0;

    let thumbContent: React.ReactNode;

    if (isAudio(attachment)) {
      thumbContent = (
        <GridAudioThumb>
          <MusicNote />
          <GridThumbLabel>{attachment.filename}</GridThumbLabel>
        </GridAudioThumb>
      );
    } else if (isPdf(attachment)) {
      thumbContent = (
        <GridPdfThumb>
          <PictureAsPdf />
          <GridThumbLabel>{attachment.filename}</GridThumbLabel>
        </GridPdfThumb>
      );
    } else if (isVideo(attachment)) {
      thumbContent = attachment.thumbnailUrl ? (
        <GridThumb src={attachment.thumbnailUrl} alt={attachment.filename} />
      ) : (
        <GridVideoThumb muted>
          <source src={attachment.contentUrl} type={attachment.mimeType} />
        </GridVideoThumb>
      );
    } else {
      thumbContent = (
        <GridThumb
          src={attachment.thumbnailUrl || attachment.contentUrl}
          alt={attachment.filename}
        />
      );
    }

    return (
      <GridItem key={attachment.id} onClick={() => openLightbox(index)}>
        {thumbContent}
        {isVideo(attachment) && !isOverflowSlot && (
          <GridVideoPlayOverlay>
            <PlayCircleFilled />
          </GridVideoPlayOverlay>
        )}
        {isOverflowSlot && <OverflowBadge>+{overflowCount}</OverflowBadge>}
      </GridItem>
    );
  };

  const currentAttachment =
    lightboxIndex !== null ? attachments[lightboxIndex] : null;

  return (
    <>
      <Grid layout={layout}>
        {visible.map((attachment, index) => renderThumb(attachment, index))}
      </Grid>

      {lightboxIndex !== null &&
        currentAttachment &&
        ReactDOM.createPortal(
          <LightboxBackdrop onClick={closeLightbox}>
            {isImage(currentAttachment) && (
              <LightboxImage
                src={currentAttachment.contentUrl}
                alt={currentAttachment.filename}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              />
            )}
            {isVideo(currentAttachment) && (
              <LightboxVideo
                controls
                autoPlay
                onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <source
                  src={currentAttachment.contentUrl}
                  type={currentAttachment.mimeType}
                />
              </LightboxVideo>
            )}
            {isAudio(currentAttachment) && (
              <LightboxAudio
                onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <AudioPlayer url={currentAttachment.contentUrl} />
              </LightboxAudio>
            )}
            {isPdf(currentAttachment) && (
              <LightboxPdfFrame
                src={currentAttachment.contentUrl}
                title={currentAttachment.filename}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              />
            )}

            {count > 1 && (
              <>
                <LightboxNavButton
                  type="button"
                  direction="left"
                  aria-label="Previous"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    goTo(lightboxIndex - 1);
                  }}>
                  <ChevronLeft />
                </LightboxNavButton>
                <LightboxNavButton
                  type="button"
                  direction="right"
                  aria-label="Next"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    goTo(lightboxIndex + 1);
                  }}>
                  <ChevronRight />
                </LightboxNavButton>
                <LightboxCounter
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                  {lightboxIndex + 1} / {count}
                </LightboxCounter>
              </>
            )}

            <LightboxActions
              onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              <LightboxIconButton
                type="button"
                aria-label="More options"
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
                onClick={() => setIsMenuOpen(o => !o)}>
                <MoreVert />
              </LightboxIconButton>
              <LightboxIconButton
                type="button"
                aria-label="Close preview"
                onClick={closeLightbox}>
                <Close />
              </LightboxIconButton>
            </LightboxActions>

            {isMenuOpen && (
              <LightboxMenu
                role="menu"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <LightboxMenuItem
                  type="button"
                  role="menuitem"
                  onClick={handleDownload}>
                  <GetApp />
                  Save as...
                </LightboxMenuItem>
              </LightboxMenu>
            )}
          </LightboxBackdrop>,
          document.body
        )}
    </>
  );
};

export default MediaGroup;
